// src/components/configuration/sections/MicrosoftIntegrationSettings.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

const MicrosoftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0">
    <path d="M11.4 2H2v9.4h9.4V2z" fill="#F25022"/>
    <path d="M22 2h-9.4v9.4H22V2z" fill="#7FBA00"/>
    <path d="M11.4 12.6H2V22h9.4v-9.4z" fill="#00A4EF"/>
    <path d="M22 12.6h-9.4V22H22v-9.4z" fill="#FFB900"/>
  </svg>
);

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Spinner } from '../../ui/spinner';

// Microsoft hook
import { useMicrosoft } from '../../../hooks/useMicrosoft';

/**
 * Unified Microsoft Integration Settings Component
 *
 * Handles OAuth for BOTH Outlook Calendar and Microsoft Teams in ONE flow
 *
 * IMPORTANT: This component handles OAuth redirects from Microsoft
 * - On mount, checks for ?success=true&section=microsoft query params
 * - Flow: Disconnected → OAuth → Connected (both features enabled)
 */
const MicrosoftIntegrationSettings = () => {
  const {
    // Connection status
    microsoftConnection,
    microsoftConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Account details
    accountEmail,
    outlookEnabled,
    teamsEnabled,

    // Disconnection
    disconnecting,

    // Operations
    loadMicrosoftConnectionStatus,
    startMicrosoftOAuth,
    disconnectMicrosoft
  } = useMicrosoft();

  // Local UI state
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState('loading'); // loading, disconnected, connected
  const [oauthError, setOauthError] = useState(null);

  // ================================
  // OAUTH REDIRECT HANDLING
  // ================================

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const errorMessage = urlParams.get('message');
    const section = urlParams.get('section');

    if (section === 'microsoft') {
      if (success === 'true') {
        console.log('✅ MicrosoftIntegrationSettings: OAuth success detected');
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => loadMicrosoftConnectionStatus(), 1000);
      } else if (error) {
        console.error('❌ MicrosoftIntegrationSettings: OAuth error detected:', error, errorMessage);
        setOauthError(errorMessage || error);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [loadMicrosoftConnectionStatus]);

  // ================================
  // DETERMINE CURRENT STEP
  // ================================

  useEffect(() => {
    if (microsoftConnectionLoading) {
      setCurrentStep('loading');
      return;
    }
    if (connected) {
      setCurrentStep('connected');
    } else {
      setCurrentStep('disconnected');
    }
  }, [connected, microsoftConnectionLoading]);

  // ================================
  // OAUTH URL GENERATED - REDIRECT
  // ================================

  useEffect(() => {
    if (oauthUrl) {
      console.log('🚀 MicrosoftIntegrationSettings: OAuth URL generated, redirecting to Microsoft...');
      window.location.href = oauthUrl;
    }
  }, [oauthUrl]);

  // ================================
  // HANDLERS
  // ================================

  const handleStartOAuth = () => {
    setOauthError(null);
    startMicrosoftOAuth();
  };

  const handleDisconnect = () => {
    disconnectMicrosoft();
    setShowDisconnectConfirm(false);
    setCurrentStep('disconnected');
  };

  // ================================
  // RENDER HELPERS
  // ================================

  const renderLoading = () => (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Spinner className="size-4" />
      <span>Loading...</span>
    </div>
  );

  const renderDisconnectedState = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Connect your Microsoft account to enable Outlook Calendar sync and Teams meeting creation.
      </p>
      {oauthError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{oauthError}</AlertDescription>
        </Alert>
      )}
      <Button onClick={handleStartOAuth} disabled={oauthInitiating}>
        {oauthInitiating ? (
          <>
            <Spinner className="size-4 mr-2" />
            Connecting...
          </>
        ) : (
          <>
            <MicrosoftIcon />
            Connect with Microsoft
          </>
        )}
      </Button>
    </div>
  );

  const renderConnectedState = () => (
    <div className="space-y-3">
      {/* Token Expired Warning */}
      {tokenExpired && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your Microsoft connection has expired. Please reconnect to continue using Calendar sync and Teams meetings.
          </AlertDescription>
        </Alert>
      )}

      {/* Connected row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
          <span className="text-sm text-foreground">{accountEmail || 'Microsoft Account'}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDisconnectConfirm(true)}
          disabled={disconnecting}
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
        </Button>
      </div>

      {/* Inline disconnect confirm */}
      {showDisconnectConfirm && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">Disconnect this account?</span>
          <button onClick={handleDisconnect} className="text-destructive hover:underline">Yes, disconnect</button>
          <button onClick={() => setShowDisconnectConfirm(false)} className="text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      )}
    </div>
  );

  // ================================
  // MAIN RENDER
  // ================================

  return (
    <div className="max-w-3xl">
      {currentStep === 'loading' && renderLoading()}
      {currentStep === 'disconnected' && renderDisconnectedState()}
      {currentStep === 'connected' && renderConnectedState()}
    </div>
  );
};

export default MicrosoftIntegrationSettings;
